import React, { useEffect, useMemo, useState } from 'react';
import { ventes as api } from '../services/api';
import { nb, montant, pct1, moisCourt, useFiltresVentes, PanneauVentes, usePanneau, SelecteurUnite, basculer } from './ventesUtils';
import { sequentielle, divergente, Echelle } from './heatmap';
import './Ventes.css';

const TITRES = { vn: 'VN — Analyse croisée', vo: 'VO — Analyse croisée' };
const MESURES = [
  { code: 'taux', label: 'Taux de marge', si: 'marge' },
  { code: 'vol', label: 'Volume' },
  { code: 'ca', label: 'CA HT' },
  { code: 'mg', label: 'Marge €', si: 'marge' },
  { code: 'mgu', label: 'Marge unitaire', si: 'marge' },
  { code: 'roole', label: 'Roole', si: 'financement' },
  { code: 'fin', label: 'Financement', si: 'financement' },
];
// Les taux se lisent face à leur moyenne pondérée : échelle divergente.
const TAUX = ['taux', 'roole', 'fin', 'mgu'];
const DIMS = [
  { code: 'concession', label: 'Concession', pl: 'concessions' }, 
  { code: 'master', label: 'Concessions groupées', pl: 'regroupements', axe: 'master' },
  { code: 'plaque', label: 'Plaque', pl: 'plaques', axe: 'plaque' },
  { code: 'marque', label: 'Marque', pl: 'marques' }, { code: 'vendeur', label: 'Vendeur', pl: 'vendeurs' },
  { code: 'famille', label: 'Famille', pl: 'familles' },
  { code: 'age', label: 'Âge de stock', pl: 'tranches d\'âge' },
];

// ── valeur d'une cellule / d'un total selon la mesure ──
// ⚠ Les trois taux se RECALCULENT depuis les cumuls : Roole = (souscrits −
// annulés) ÷ volume des PARTICULIERS (`vp`, 2026-10-01 : Roole ne se mesure
// que sur eux), financement = montants financés ÷ CA TTC. Additionner ou
// moyenner des pourcentages de mois à volumes différents donne un faux.
const som = (a) => a.reduce((x, y) => x + y, 0);
// Taux de marge : opérandes hors ventes en mode agent (catx / mgtx, serveur).
const txCa = (o) => o.catx || o.ca, txMg = (o) => o.mgtx || o.mg;
const cellVal = (o, i, m) => {
  if (m === 'vol') return o.vol[i];
  if (m === 'ca') return o.ca[i];
  if (m === 'mg') return o.mg[i];
  // Marge unitaire : marge ÷ volume. Elle se RECALCULE à chaque niveau, jamais
  // ne se somme — c'est un rapport, comme les taux.
  if (m === 'mgu') return o.vol[i] ? o.mg[i] / o.vol[i] : null;
  if (m === 'roole') { const vp = (o.vp || [])[i] || 0; return vp > 0 ? (o.rl[i] / vp) * 100 : null; }
  if (m === 'fin') return o.cattc[i] ? (o.fin[i] / o.cattc[i]) * 100 : null;
  return txCa(o)[i] ? (txMg(o)[i] / txCa(o)[i]) * 100 : null;
};
function totVal(o, m) {
  const sv = som(o.vol), sc = som(o.ca), sm = som(o.mg);
  if (m === 'vol') return sv;
  if (m === 'ca') return sc;
  if (m === 'mg') return sm;
  if (m === 'mgu') return sv ? sm / sv : null;
  if (m === 'roole') { const r = som(o.rl || []), vp = som(o.vp || []); return vp > 0 ? (r / vp) * 100 : null; }
  if (m === 'fin') { const t = som(o.cattc || []); return t ? (som(o.fin || []) / t) * 100 : null; }
  const tc = som(txCa(o)), tm = som(txMg(o));
  return tc ? (tm / tc) * 100 : null;
}
const MONETAIRES = ['ca', 'mg'];
const fmt = (v, m, u) => (v == null ? '—' : m === 'mgu' ? montant(v, 'e') : TAUX.includes(m) ? pct1(v) : m === 'vol' ? nb(v) : montant(v, u));

// Un taux se lit face à sa moyenne pondérée : échelle divergente. Le reste
// (volume, CA, marge €) n'a pas de point neutre : échelle séquentielle.
// Les deux viennent de `heatmap.js` — cf. CLAUDE.md §5.
const couleur = (v, m, st) => (TAUX.includes(m) ? divergente(v, st) : sequentielle(v, st.max));
function statsDe(rows, m) {
  const vals = []; let num = 0, den = 0;
  rows.forEach((r) => r.vol.forEach((_, i) => {
    const v = cellVal(r, i, m); if (v != null) vals.push(v);
    // Le point neutre de l'échelle divergente est la moyenne PONDÉRÉE de la
    // mesure, pas la moyenne des cellules.
    if (m === 'mgu') { num += r.mg[i]; den += r.vol[i]; }
    else if (m === 'roole') { num += (r.rl || [])[i] || 0; den += (r.vp || [])[i] || 0; }
    else if (m === 'fin') { num += (r.fin || [])[i] || 0; den += (r.cattc || [])[i] || 0; }
    else { num += txMg(r)[i]; den += txCa(r)[i]; }
  }));
  return { min: Math.min(...vals, 0), max: Math.max(...vals, 1), mid: TAUX.includes(m) ? (den ? (num / den) * (m === 'mgu' ? 1 : 100) : 0) : 0 };
}
// `branche` : cf. VentesPerformance. Sans marge publiée (VN métropole), les
// mesures « Taux de marge » et « Marge € » sont retirées et la mesure par
// défaut devient le volume.
export default function VentesAnalyse({ type = 'vn', branche = 'auto-antilles' }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [mesure, setMesure] = useState('taux');
  const [unite, setUnite] = useState('k');
  const [dimension, setDimension] = useState('marque');
  const [drill, setDrill] = useState({}); // { [marque]: rows | 'loading' }
  const [open, setOpen] = useState(null);
  const [ouvert, toggle] = usePanneau();
  const societesDispo = useMemo(() => data?.societesDispo || [], [data]);
  const f = useFiltresVentes(societesDispo);

  useEffect(() => {
    let actif = true; setErreur(''); setOpen(null); setDrill({});
    api.analyse(type, { periode: f.periode, mois: f.moisStr, societes: f.societesUrl, dimension, branche })
      .then((r) => {
        if (!actif) return;
        setData(r.data);
        if (r.data.margeDisponible === false) setMesure((m) => (m === 'taux' || m === 'mg' ? 'vol' : m));
      })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [type, branche, f.periode, f.moisStr, f.societesUrl, dimension]);

  // societesDispo peut manquer si le back ne le renvoie pas ici : on le récupère
  // sur le 1er chargement via un appel performance léger n'est pas nécessaire —
  // analyse renvoie societesDispo ? sinon fallback vide (filtre sociétés inactif).
  const ouvrirDrill = async (marque) => {
    if (open === marque) { setOpen(null); return; }
    setOpen(marque);
    if (!drill[marque]) {
      setDrill((d) => ({ ...d, [marque]: 'loading' }));
      try {
        const r = await api.analyse(type, { periode: f.periode, mois: f.moisStr, societes: f.societesUrl, dimension: 'marque', drill: marque, branche });
        setDrill((d) => ({ ...d, [marque]: r.data.rows || [] }));
      } catch (e) { setDrill((d) => ({ ...d, [marque]: [] })); }
    }
  };

  const Panneau = <PanneauVentes ouvert={ouvert} onToggle={toggle} f={f} societesDispo={societesDispo} />;
  if (erreur) return <><main className="vt-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;

  const { mois, rows } = data;
  const avecMarge = data.margeDisponible !== false;
  const avecFin = data.financementDisponible !== false;
  // Une mesure que le périmètre ne porte pas n'est pas proposée : Roole et le
  // financement sont des produits antillais, à 0 % en métropole.
  const dispo = { marge: avecMarge, financement: avecFin };
  const mesures = MESURES.filter((m) => !m.si || dispo[m.si]);
  // ⚠ Une mesure retenue puis devenue indisponible (changement de branche)
  // laisserait la matrice vide sans rien dire : on retombe sur la première.
  const mes = mesures.some((m) => m.code === mesure) ? mesure : (mesures[0] || {}).code;
  const st = statsDe(rows, mes);
  const dimObj = DIMS.find((d) => d.code === dimension);
  const drillable = data.drillable;

  // Les pieds se recalculent comme les cellules : une ligne fictive qui somme
  // toutes les lignes, passée aux MÊMES fonctions. Deux chemins de calcul, ce
  // sont deux occasions de diverger.
  const cumul = (extrait) => mois.map((_, i) => rows.reduce((t, r) => t + (extrait(r)[i] || 0), 0));
  const ensemble = {
    vol: cumul((r) => r.vol), ca: cumul((r) => r.ca), mg: cumul((r) => r.mg),
    catx: cumul((r) => txCa(r)), mgtx: cumul((r) => txMg(r)),
    cattc: cumul((r) => r.cattc || []), rl: cumul((r) => r.rl || []), vp: cumul((r) => r.vp || []), fin: cumul((r) => r.fin || []),
  };
  const totMois = mois.map((_, i) => cellVal(ensemble, i, mes));
  const totGen = totVal(ensemble, mes);

  // Concession, regroupement et plaque filtrent la page au clic ; seule la
  // marque se déplie (CLAUDE.md §4 bis : filtrer et déplier ne partagent pas
  // la même cible). Un regroupement renvoie la LISTE de ses membres, que le
  // filtre `societes` accepte telle quelle.
  const dimObjCourant = DIMS.find((d) => d.code === dimension) || {};
  const filtrable = dimension === 'concession' || !!dimObjCourant.axe;
  const cleDe = (o) => (dimObjCourant.axe
    ? (societesDispo || []).filter((x) => x[dimObjCourant.axe] === o.nom).map((x) => x.code).join(',')
    : ((societesDispo || []).find((x) => x.nom === o.nom) || {}).code || '');

  const Cellule = ({ o, i }) => {
    const v = cellVal(o, i, mes), c = couleur(v, mes, st);
    return <td style={{ background: c.bg }}><span className={`cell${c.mut ? ' mut' : ''}`} style={c.fg ? { color: c.fg } : undefined}>{fmt(v, mes, unite)}</span></td>;
  };

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div>
            <h1>{TITRES[type]}</h1>
          </div>
          <div className="vt-asof"><b>{data.periode || ''}</b></div>
        </div>

        <div className="vt-ctl">
          <span><span className="lab">Mesure</span>
            <span className="vt-seg">{mesures.map((m) => <button key={m.code} className={mesure === m.code ? 'on' : ''} onClick={() => setMesure(m.code)}>{m.label}</button>)}</span>
          </span>
          <span><span className="lab">Dimension</span>
            <span className="vt-seg">{DIMS.map((d) => {
              // Un axe de regroupement vide n'est pas proposé : un bouton qui
              // ne produit rien se lit comme une panne.
              const off = d.axe && !(societesDispo || []).some((x) => x[d.axe]);
              return (
                <button key={d.code} disabled={off} className={dimension === d.code ? 'on' : ''}
                  title={off ? `Aucune valeur « ${d.label} » dans le référentiel des sociétés` : ''}
                  onClick={() => setDimension(d.code)}>{d.label}</button>
              );
            })}</span>
          </span>
        </div>

        <div className="vt-card">
          <div className="vt-card-h"><h2>{(mesures.find((m) => m.code === mes) || {}).label} — par {dimObj.label.toLowerCase()} et par mois</h2>
            <div className="st-tools">{data.tronque && <span className="hint">{nb(rows.length - 1)} premiers sur {nb(data.tronque)} par volume, le reste en « Autres »</span>}
              {!avecMarge && <span className="hint">marge non publiée sur cette branche</span>}
              {MONETAIRES.includes(mes) && <SelecteurUnite unite={unite} setUnite={setUnite} />}
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-mx tbl-dense">
              <thead><tr><th className="l">{dimObj.label}</th>{mois.map((m) => <th key={m}>{moisCourt(m)}</th>)}<th className="tot">{TAUX.includes(mes) ? 'Moy.' : 'Total'}</th></tr></thead>
              <tbody>
                {rows.map((o) => {
                  const isOpen = open === o.nom;
                  const sub = drill[o.nom];
                  return (
                    <React.Fragment key={o.nom}>
                      <tr className={filtrable ? `tbl-clic${f.societesUrl === cleDe(o) ? ' actif' : ''}` : undefined}
                        onClick={filtrable ? () => f.setFiltre({ societes: basculer(f.societesUrl, cleDe(o)) }) : undefined}>
                        <td className={`l ${drillable ? '' : 'nodrill'} ${isOpen ? 'open' : ''}`}
                          onClick={drillable ? () => ouvrirDrill(o.nom) : undefined}>
                          <span className="chev">{drillable ? '▶' : ''}</span>{o.nom}
                        </td>
                        {mois.map((_, i) => <Cellule key={i} o={o} i={i} />)}
                        <td className="tot">{fmt(totVal(o, mes), mes, unite)}</td>
                      </tr>
                      {isOpen && sub === 'loading' && <tr className="sub"><td className="l"><span className="chev" />Chargement…</td><td colSpan={mois.length + 1} /></tr>}
                      {isOpen && Array.isArray(sub) && sub.map((mo) => (
                        <tr className="sub" key={mo.nom}>
                          <td className="l" title={mo.nom}><span className="chev" />{mo.nom}</td>
                          {mois.map((_, i) => <Cellule key={i} o={mo} i={i} />)}
                          <td className="tot">{fmt(totVal(mo, mes), mes, unite)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot><tr><td className="l">Total / moy.</td>{totMois.map((v, i) => <td key={i}>{fmt(v, mes, unite)}</td>)}<td className="tot">{fmt(totGen, mes, unite)}</td></tr></tfoot>
            </table>
            <div className="vt-lgd">
              {TAUX.includes(mes) ? (
                <><span>Sous la moyenne</span><Echelle divergent /><span>Au-dessus</span>&nbsp;·&nbsp;moyenne groupe : <b>{mes === 'mgu' ? montant(st.mid, 'e') : pct1(st.mid)}</b></>
              ) : (
                <><span>Faible</span><Echelle /><span>Élevé</span></>
              )}
            </div>
          </div>
        </div>
      </main>
      {Panneau}
    </>
  );
}
